import { useEffect, useState } from 'react';
import { ensurePushSubscription } from '../lib/push';

const VAPID_PUBLIC = import.meta.env.VITE_VAPID_PUBLIC_KEY ?? '';
const DISMISS_KEY = 'cpe.push-banner-dismissed';

type State = 'unsupported' | 'unavailable' | 'denied' | 'default' | 'granted';

function detectState(): State {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'unsupported';
  }
  if (!VAPID_PUBLIC) return 'unavailable';
  return Notification.permission as State;
}

export function PushPermissionBanner() {
  const [state, setState] = useState<State>(() => detectState());
  const [dismissed, setDismissed] = useState<boolean>(() => localStorage.getItem(DISMISS_KEY) === '1');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-detect state when window regains focus (user may have changed permissions in OS settings).
  useEffect(() => {
    const onFocus = () => setState(detectState());
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  if (state === 'granted' || state === 'unsupported') return null;
  if (state === 'unavailable') return null;
  if (dismissed && state !== 'denied') return null;

  const onActivate = async () => {
    setBusy(true);
    setError(null);
    try {
      const ok = await ensurePushSubscription();
      if (!ok) {
        setError('No se pudo activar. Verifica los permisos del navegador.');
      }
      setState(detectState());
    } finally {
      setBusy(false);
    }
  };

  const onDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  if (state === 'denied') {
    return (
      <div className="bg-rose-50 border-2 border-rose-300 rounded-2xl p-4 text-base text-rose-900">
        <p className="font-semibold">🔕 Notificaciones bloqueadas</p>
        <p className="text-sm mt-1">
          Tienes las notificaciones bloqueadas en este navegador. Para activarlas, ve a la configuración de la app
          o del navegador y permite las notificaciones de CPE Créditos.
        </p>
      </div>
    );
  }

  // 'default' — never asked, or asked but dismissed
  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 flex items-start gap-3">
      <div className="flex-1 text-amber-900">
        <p className="font-semibold text-base">🔔 Activa las notificaciones</p>
        <p className="text-sm mt-1">
          Recibe avisos cuando llegue un mensaje o documento de crédito, aunque tengas la app cerrada.
        </p>
        {error && <p className="text-sm mt-2 text-rose-700 font-medium">{error}</p>}
      </div>
      <div className="flex flex-col gap-2 shrink-0">
        <button
          onClick={onActivate}
          disabled={busy}
          className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white text-sm font-bold rounded-full whitespace-nowrap"
        >
          {busy ? '…' : 'Activar'}
        </button>
        <button
          onClick={onDismiss}
          className="text-xs text-amber-800 hover:text-amber-950 font-medium"
        >
          Más tarde
        </button>
      </div>
    </div>
  );
}
